import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Printer } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/i18n";
import { Button } from "@/components/ui/button";

const MONTHS = {
  nl: ["januari", "februari", "maart", "april", "mei", "juni", "juli", "augustus", "september", "oktober", "november", "december"],
  en: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
  es: ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"],
};
const WD = { nl: ["Z", "M", "D", "W", "D", "V", "Z"], en: ["S", "M", "T", "W", "T", "F", "S"], es: ["D", "L", "M", "X", "J", "V", "S"] };

const L = {
  nl: { title: "Maandplanning", total: "Totaal", reserved: "Gereserveerd", occupied: "Bezet", history: "Historiek", broken: "Loopt door buiten de maand", print: "Afdrukken", pitch: "Plaats" },
  en: { title: "Monthly planning", total: "Total", reserved: "Reserved", occupied: "Occupied", history: "History", broken: "Continues outside this month", print: "Print", pitch: "Pitch" },
  es: { title: "Planificación mensual", total: "Total", reserved: "Reservado", occupied: "Ocupado", history: "Historial", broken: "Continúa fuera del mes", print: "Imprimir", pitch: "Parcela" },
};

const addDays = (iso, n) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

/** Maandplanning, liggend op A3, om achter de balie te hangen. */
export default function PlanningPrint() {
  const { lang: uiLang } = useLang();
  const [params] = useSearchParams();
  const lang = params.get("lang") || uiLang;
  const l = L[params.get("lang") || uiLang] || L.en;
  const month = params.get("month") || new Date().toISOString().slice(0, 7);
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => setData((await api.get(`/planning/month?month=${month}`)).data))();
  }, [month]);

  useEffect(() => { if (data) setTimeout(() => window.print(), 500); }, [data]);

  const title = useMemo(() => {
    const [y, m] = month.split("-").map(Number);
    return `${(MONTHS[lang] || MONTHS.en)[m - 1]} ${y}`;
  }, [month, lang]);

  if (!data) return <div className="p-8 text-sm text-slate-500">…</div>;

  const cols = Array.from({ length: data.days }, (_, i) => {
    const iso = addDays(data.start, i);
    const wd = new Date(`${iso}T00:00:00Z`).getUTCDay();
    return { i, iso, wd, weekend: wd === 0 || wd === 6 };
  });

  const cellsFor = (row) => {
    const out = [];
    let at = 0;
    for (const b of row.bookings) {
      if (b.col > at) out.push({ free: true, col: at, len: b.col - at });
      if (b.col + b.len > at) out.push({ ...b, col: Math.max(at, b.col), len: b.col + b.len - Math.max(at, b.col) });
      at = Math.max(at, b.col + b.len);
    }
    if (at < data.days) out.push({ free: true, col: at, len: data.days - at });
    return out;
  };

  return (
    <div className="bg-white text-slate-900 p-4 print:p-0" data-testid="planning-print-page">
      <style>{"@page { size: A3 landscape; margin: 8mm; } @media print { .no-print { display: none !important; } }"}</style>

      <div className="flex items-end justify-between mb-2">
        <div>
          <h1 className="font-display font-extrabold text-2xl leading-none" data-testid="planning-print-title">
            {l.title} — {title}
          </h1>
          <p className="text-[10px] text-slate-500 mt-1">
            {l.total}: {data.total_nights}/{data.capacity} · {data.pct}%
          </p>
        </div>
        <div className="flex items-center gap-3 text-[9px] text-slate-700">
          <span className="flex items-center gap-1"><i className="inline-block h-2.5 w-4 rounded-sm" style={{ background: "#f59e0b" }} />{l.reserved}</span>
          <span className="flex items-center gap-1"><i className="inline-block h-2.5 w-4 rounded-sm" style={{ background: "#e11d48" }} />{l.occupied}</span>
          <span className="flex items-center gap-1"><i className="inline-block h-2.5 w-4 rounded-sm" style={{ background: "#cbd5e1" }} />{l.history}</span>
          <span className="flex items-center gap-1"><i className="inline-block h-2.5 w-4 rounded-sm border-l-2 border-dashed border-slate-900" style={{ background: "#e2e8f0" }} />{l.broken}</span>
          <Button size="sm" className="no-print ml-2" onClick={() => window.print()} data-testid="planning-print-now"><Printer className="h-4 w-4 mr-1" />{l.print}</Button>
        </div>
      </div>

      <table className="w-full border-collapse" style={{ tableLayout: "fixed" }} data-testid="planning-print-table">
        <colgroup>
          <col style={{ width: "42px" }} />
          {cols.map((c) => <col key={c.i} />)}
          <col style={{ width: "34px" }} />
        </colgroup>
        <thead>
          <tr>
            <th className="border border-slate-400 bg-slate-100 text-[8px] uppercase tracking-wider">{l.pitch}</th>
            {cols.map((c) => (
              <th key={c.i} className={`border border-slate-400 text-[8px] leading-[1.1] ${c.weekend ? "bg-slate-200" : "bg-slate-50"}`}>
                <div className="tabular-nums font-bold">{c.iso.slice(8, 10)}</div>
                <div className="text-slate-500">{(WD[lang] || WD.en)[c.wd]}</div>
              </th>
            ))}
            <th className="border border-slate-400 bg-slate-100 text-[8px]">%</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r) => (
            <tr key={r.id} data-testid={`planning-print-row-${r.label}`} style={{ height: "15px" }}>
              <td className="border border-slate-400 bg-slate-50 text-[8px] font-bold text-center">{r.label}</td>
              {cellsFor(r).map((c, k) => c.free ? (
                cols.slice(c.col, c.col + c.len).map((d) => (
                  <td key={`f${d.i}`} className={`border border-slate-300 ${d.weekend ? "bg-slate-100" : ""}`} />
                ))
              ) : (
                <td key={`b${k}`} colSpan={c.len} className="border border-slate-400 p-0 align-middle"
                  data-testid={`planning-print-bar-${c.id}`}
                  style={{
                    background: c.code === "B" ? "#e11d48" : c.code === "O" ? "#cbd5e1" : "#f59e0b",
                    borderLeft: c.clipped_left ? "3px dashed #0f172a" : undefined,
                    borderRight: c.clipped_right ? "3px dashed #0f172a" : undefined,
                    boxShadow: c.ribbon ? "inset 0 0 0 2px #4c0519" : undefined,
                  }}>
                  <div className={`truncate px-1 text-[8px] font-bold leading-none text-center ${c.code === "O" ? "text-slate-700" : "text-white"}`}>
                    {c.clipped_left ? "◀ " : ""}{c.last_name || c.guest_name}{c.clipped_right ? " ▶" : ""}
                  </div>
                </td>
              ))}
              <td className="border border-slate-400 text-[8px] tabular-nums text-center">{Math.round(r.pct)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
